import React, { createContext, useContext, useMemo, PropsWithChildren } from 'react';
import { isE2BEnvironment } from '@/utils/environment';

/**
 * App Mode Types
 * 
 * - NATIVE_TAB: Running inside Access Workspace iframe (production)
 * - STANDALONE: Running in direct browser access (development/testing)
 * - E2B: Running in e2b.app environment (AI agent sandboxed development)
 * 
 * All modes use real OIDC/JWT authentication.
 * E2B mode differs only in URL construction (dynamic instance IDs) and CORS handling.
 */
export enum AppMode {
  NATIVE_TAB = 'native-tab',
  STANDALONE = 'standalone',
  E2B = 'e2b',
  DEV_AUTH = 'dev-auth'
}

interface AppModeContextValue {
  mode: AppMode;
  isNativeTab: boolean;
  isStandalone: boolean;
  isE2B: boolean;
  isDevAuth: boolean;
}

const AppModeContext = createContext<AppModeContextValue | undefined>(undefined);

/**
 * App Mode Provider
 * 
 * Detects the deployment environment to apply appropriate URL construction and CORS handling:
 * - Native Tab Mode: Inside Access Workspace iframe (silent OIDC authentication)
 * - Standalone Mode: Direct browser access (interactive OIDC authentication)
 * - E2B Mode: e2b.app sandboxed environment (interactive OIDC with backend proxy for CORS)
 * 
 * Detection Logic:
 * - if (window.self !== window.top && !isE2BEnvironment()) → Native Tab Mode
 * - else if (window.self === window.top && !isE2BEnvironment()) → Standalone Mode
 * - else (isE2BEnvironment()) → E2B Mode
 * 
 * All modes use real JWT authentication. E2B is treated like Standalone for auth purposes.
 * This detection happens ONCE on mount and never changes.
 */
export function AppModeProvider({ children }: PropsWithChildren) {
  const value = useMemo(() => {
    // Check if running in iframe
    const isInIframe = window.self !== window.top;
    
    // Check if running in e2b.app environment
    const isE2BEnv = isE2BEnvironment();
    
    // Check if DevAuth mode is enabled (backend handles auth, skip OIDC)
    const isDevAuth = import.meta.env.VITE_DEV_AUTH === 'true';

    // Determine mode based on conditions
    let mode: AppMode;
    if (isDevAuth) {
      mode = AppMode.DEV_AUTH;
    } else if (isInIframe && !isE2BEnv) {
      mode = AppMode.NATIVE_TAB;
    } else if (!isInIframe && !isE2BEnv) {
      mode = AppMode.STANDALONE;
    } else {
      mode = AppMode.E2B;
    }

    return {
      mode,
      isNativeTab: mode === AppMode.NATIVE_TAB,
      isStandalone: mode === AppMode.STANDALONE,
      isE2B: mode === AppMode.E2B,
      isDevAuth: mode === AppMode.DEV_AUTH,
    };
  }, []); // Empty deps - detect once on mount

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
}

/**
 * Hook to access app mode
 * 
 * Use this hook to conditionally render or execute code based on deployment mode.
 * All modes use real OIDC/JWT authentication.
 * 
 * @example
 * const { mode, isNativeTab, isStandalone, isE2B } = useAppMode();
 * 
 * if (isNativeTab) {
 *   // Use workspace hooks for silent authentication
 *   const settings = useProductSettings();
 * }
 * 
 * if (isStandalone || isE2B) {
 *   // Use interactive redirect authentication
 *   // E2B also uses backend proxy for OIDC config (CORS bypass)
 * }
 */
export function useAppMode() {
  const context = useContext(AppModeContext);
  
  if (!context) {
    throw new Error('useAppMode must be used within AppModeProvider');
  }
  
  return context;
}
